import type { Theme } from '@mui/material/styles';

interface IFileListsStyleOptions {
  override?: Record<string, any>;
  isDragActive?: boolean;
}

export const useFileListsStyles = (
  theme: Theme,
  { override, isDragActive }: IFileListsStyleOptions = {}
) => {
  // Shared pill-style button used across the module.
  const pillButton = {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 600,
  };
  const pillButtonWide = {
    ...pillButton,
    px: 3,
    flexShrink: 0,
  };

  return {
    render: {
      root: {
        width: '100% !important',
        padding: `${theme.spacing(1)} !important`,
        fontSize: '1.0rem !important',
        containerType: 'inline-size',
        ...override?.root,
      },

      headerCard: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        '@container (min-width: 480px)': { p: 2 },
        mb: 2,
      },
      headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        mb: 3,
      },
      title: {
        fontWeight: 600,
      },
      manageTagsButton: pillButton,

      uploadWrap: {
        mb: 2,
      },
      dropzone: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: 1.5,
        py: 1.5,
        '@container (min-width: 480px)': { px: 2.5, py: 2 },
        border: '1px solid',
        borderColor: isDragActive ? 'primary.main' : 'divider',
        borderRadius: 2,
        backgroundColor: isDragActive ? 'action.hover' : 'transparent',
        transition: 'border-color 0.15s, background-color 0.15s',
      },
      dropzoneRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        '@container (min-width: 720px)': { flexWrap: 'nowrap' },
      },
      dropzoneMain: {
        flex: 1,
        minWidth: 0,
        maxWidth: '100%',
        width: '100%',
        '@container (min-width: 720px)': { width: 'auto' },
      },
      dropzoneIcon: {
        color: 'text.secondary',
      },
      dropzoneUploadingText: {
        flex: 1,
        color: 'text.primary',
        fontWeight: 600,
        fontSize: '16px',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      dropzoneFileName: {
        fontWeight: 500,
        fontSize: '16px',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      dropzonePromptText: {
        color: 'text.secondary',
        fontSize: '16px',
      },
      dropzoneBrowse: {
        color: 'primary.main',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '16px',
        '&:hover': { textDecoration: 'underline' },
      },
      dropzoneSelect: {
        width: '100%',
        minWidth: 0,
        '@container (min-width: 720px)': { width: 180, minWidth: 180 },
        flexShrink: 0,
      },
      // react-select `styles` prop for the tag picker in the dropzone.
      select: {
        placeholder: (base: any) => ({
          ...base,
          fontSize: '16px',
        }),
        singleValue: (base: any) => ({
          ...base,
          maxWidth: 'calc(100% - 8px)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }),
      },
      uploadButton: {
        ...pillButton,
        px: 3,
        flexShrink: 0,
        width: '100%',
        '@container (min-width: 720px)': { width: 'auto' },
      },
      uploadError: {
        mt: 1,
      },
      uploadProgressWrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      },
      uploadProgressBar: {
        width: '100%',
        height: 8,
        borderRadius: 999,
        backgroundColor: 'action.hover',
        '& .MuiLinearProgress-bar': {
          borderRadius: 999,
        },
      },
      uploadProgressLabel: {
        alignSelf: 'flex-end',
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: 'text.secondary',
        fontVariantNumeric: 'tabular-nums',
      },

      toolbarRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        mb: 2.5,
      },
      filterTabList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
      },
      filterTab: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.75,
        minWidth: 88,
        maxWidth: 130,
        '@container (min-width: 480px)': {
          minWidth: 110,
          maxWidth: 150,
          px: 1.5,
        },
        font: 'inherit',
        fontSize: '14px',
        fontWeight: 600,
        color: 'text.primary',
        backgroundColor: 'transparent',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 999,
        px: 1,
        py: 0.75,
        cursor: 'pointer',
        transition: 'background-color .12s, border-color .12s',
        '&:hover': { backgroundColor: 'action.hover' },
      },
      filterTabLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
      },
      filterTabActive: {
        backgroundColor: 'action.selected',
        borderColor: 'text.primary',
        '&:hover': { backgroundColor: 'action.selected' },
      },
      filterCount: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        minWidth: 20,
        height: 20,
        px: 0.75,
        borderRadius: 999,
        fontSize: '0.6875rem',
        fontWeight: 700,
        lineHeight: 1,
        color: 'text.secondary',
        backgroundColor: 'action.selected',
        fontVariantNumeric: 'tabular-nums',
      },
      filterCountActive: {
        color: 'text.primary',
      },
      bulkActions: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
      },
      selectAllControl: {
        mr: 0,
      },
      selectAllLabel: {
        fontSize: '14px',
        fontWeight: 500,
      },
      deleteSelectedButton: {
        ...pillButton,
        fontSize: '14px',
      },

      emptyState: {
        color: 'text.secondary',
        fontSize: '0.875rem',
        py: 3,
        textAlign: 'center',
      },

      groupBlock: {
        mt: 3,
        pt: 3,
        borderTop: '1px solid',
        borderColor: 'divider',
      },
      groupHead: {
        fontWeight: 700,
        mb: 0.5,
      },

      fileRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 0.75,
        py: 1,
        '@container (min-width: 480px)': {
          gap: 1.75,
          px: 1.25,
          py: 1.25,
        },
        borderRadius: 2,
        transition: 'background-color .1s',
        '&:hover': { backgroundColor: 'action.hover' },
      },
      rowCheckbox: {
        flex: '0 0 auto',
        p: 0.25,
        '@container (min-width: 480px)': { p: 0.5 },
      },
      fileIcon: {
        flex: '0 0 auto',
        width: 30,
        height: 30,
        '@container (min-width: 480px)': { width: 36, height: 36 },
        display: 'grid',
        placeItems: 'center',
        borderRadius: 2,
      },
      fileIconTinted: (fg: string, bg: string) => ({
        flex: '0 0 auto',
        width: 30,
        height: 30,
        '@container (min-width: 480px)': { width: 36, height: 36 },
        display: 'grid',
        placeItems: 'center',
        borderRadius: 2,
        color: fg,
        bgcolor: bg,
      }),
      fileMain: {
        minWidth: 0,
        gap: 0.25,
        flex: 1,
      },
      fileName: {
        fontSize: '0.8125rem',
        '@container (min-width: 480px)': { fontSize: '0.875rem' },
        fontWeight: 600,
        color: 'text.primary',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      fileMeta: {
        fontSize: '0.78rem',
        color: 'text.secondary',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.25,
        fontVariantNumeric: 'tabular-nums',
      },
      metaSep: {
        color: 'divider',
      },
      metaUploader: {
        color: 'primary.main',
        fontWeight: 500,
      },
      fileActions: {
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        '@container (min-width: 480px)': { gap: 0.5 },
      },
      processingIndicator: {
        px: 1,
        color: 'text.secondary',
      },
      deleteAction: {
        '&:hover': { color: 'error.main' },
      },
    },

    tagManagement: {
      root: {
        p: 1.5,
        '@container (min-width: 480px)': { p: 2 },
        mb: 2,
        borderRadius: 4,
        // backgroundColor: '#f5f5f5',
      },
      loaderWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
      },
      headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
      },
      addTagRow: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        '@container (min-width: 720px)': {
          flexDirection: 'row',
          alignItems: 'center',
        },
        gap: 1.5,
        mb: 3,
      },
      addTagButton: {
        ...pillButtonWide,
        height: 36,
        width: '100%',
        '@container (min-width: 720px)': { width: 'auto' },
      },
      // Live theme forces a fixed 23px input height + 10px padding on all
      // outlined inputs regardless of `size`; override it to match addTagButton's height.
      addTagInput: {
        '& .MuiOutlinedInput-input': {
          height: '16px !important',
        },
      },
      allTagsLabel: {
        mb: 1,
      },
      chipList: {
        display: 'flex',
        flexWrap: 'wrap',
        mb: 3,
        gap: 1,
      },
      chip: {
        borderRadius: '16px',
        cursor: 'pointer',
        borderColor: theme.palette.divider,
        color: theme.palette.text.primary,
        '& .MuiChip-deleteIcon': {
          color: theme.palette.text.secondary,
          '&:hover': { color: theme.palette.text.primary },
        },
      },
      siteCard: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        '@container (min-width: 480px)': { p: 2 },
        mb: 2,
      },
      siteHint: {
        mb: 1.5,
      },
      saveRow: {
        display: 'flex',
        justifyContent: 'stretch',
        '@container (min-width: 480px)': { justifyContent: 'flex-end' },
        mt: 2,
      },
      saveButton: {
        ...pillButtonWide,
        width: '100%',
        '@container (min-width: 480px)': { width: 'auto' },
      },
      // react-select `styles` prop for the multi-select tag picker.
      select: {
        placeholder: (base: any) => ({
          ...base,
          fontSize: '16px',
        }),
        multiValue: (base: any) => ({
          ...base,
          borderRadius: '16px',
          paddingLeft: '6px',
          alignItems: 'center',
        }),
        multiValueLabel: (base: any) => ({
          ...base,
          borderRadius: '16px',
          fontWeight: 500,
        }),
        multiValueRemove: (base: any) => ({
          ...base,
          borderRadius: '50%',
          width: '18px',
          height: '18px',
          padding: 0,
          margin: '0 4px',
          justifyContent: 'center',
          backgroundColor: '#c4c4c4',
          color: '#fff',
          ':hover': {
            backgroundColor: theme.palette.error.main,
            color: '#fff',
          },
        }),
      },
    },

    editTagModal: {
      fieldWrap: {
        pt: 0.5,
        pb: 2,
      },
      actionsRow: {
        display: 'flex',
        justifyContent: 'flex-end',
      },
      updateButton: {
        borderRadius: 1.5,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.75rem',
        boxShadow: 'none',
      },
    },
  };
};
